import StorageService from '../services/StorageService';

const CURRENT_ROLE_KEY = 'current_active_role';

// Role mapping
export const ROLE_TYPES = {
  BUYER: { 
    id: 1, 
    name: 'Buyer', 
    route: '/dashboard/buyer', 
    profileRoute: '/buyer-profile',
    viewProfileRoute: '/profile/view' 
  },
  SELLER: { 
    id: 2, 
    name: 'Seller', 
    route: '/dashboard/seller', 
    profileRoute: '/seller-profile',
    viewProfileRoute: '/profile/view' 
  },
  QUALITY_INSPECTOR: {
    id: 'quality_inspector',
    name: 'Quality Inspector',
    route: '/auction/qa/dashboard',
    profileRoute: '/quality-inspector-profile',
    viewProfileRoute: '/profile/view',
  },
  QUALITY_INSPECTOR_SERVICE: {
    id: 'quality_inspector_service',
    name: 'Quality Inspector Service',
    route: '/quality-inspector-service/dashboard',
    profileRoute: '/quality-inspector-service-profile',
    viewProfileRoute: '/profile/view',
  },
  LOGISTICS: {
    id: 'logistics',
    name: 'Logistics',
    route: '/dashboard/logistics',
    profileRoute: '/logistics-profile/edit',
    viewProfileRoute: '/logistics-profile',
  },
  LOGISTICS_MANAGER: {
    id: 'logistics_manager',
    name: 'Logistics Manager',
    route: '/dashboard/logistics-manager/business-relocation-rfqs',
    profileRoute: '/home',
    viewProfileRoute: '/home',
  },
  LOGISTICS_USER: {
    id: 'logistics_user',
    name: 'Logistics',
    route: '/dashboard/logistics-manager/business-relocation-rfqs',
    profileRoute: '/home',
    viewProfileRoute: '/home',
  },
  SUPPORT_MANAGER: {
    id: 'support_manager',
    name: 'Support Manager',
    route: '/dashboard/support-manager/disputes',
    profileRoute: '/home',
    viewProfileRoute: '/home',
  },
  SUPPORT_USER: {
    id: 'support_user',
    name: 'Support',
    route: '/dashboard/support/disputes',
    profileRoute: '/home',
    viewProfileRoute: '/home',
  },
};

export function isLogisticsManagerUser(user = StorageService.getUser()) {
  if (!user) return false;
  return (
    user.access_role_level === 'logistics_manager' ||
    user.access_role_code === 'logistics-manager' ||
    user.access_account_type === 'logistics_manager'
  );
}

export function isSupportManagerUser(user = StorageService.getUser()) {
  if (!user) return false;
  return (
    user.access_role_level === 'support_manager' ||
    user.access_role_code === 'support-manager' ||
    user.access_account_type === 'support_manager'
  );
}

export function isLogisticsUserAccount(user = StorageService.getUser()) {
  if (!user) return false;
  if (isLogisticsManagerUser(user)) return false;
  return (
    user.access_role_level === 'logistics_user' ||
    user.access_role_code === 'logistics' ||
    user.access_account_type === 'logistics_user'
  );
}

export function isSupportUserAccount(user = StorageService.getUser()) {
  if (!user) return false;
  if (isSupportManagerUser(user) || isLogisticsManagerUser(user) || isLogisticsUserAccount(user)) {
    return false;
  }
  return (
    user.access_role_level === 'support_user' ||
    user.access_role_code === 'support' ||
    user.access_account_type === 'support_user'
  );
}

export function isLogisticsStaffUser(user = StorageService.getUser()) {
  return isLogisticsManagerUser(user) || isLogisticsUserAccount(user);
}

export function isPlatformAccessStaffUser(user = StorageService.getUser()) {
  return (
    isSupportManagerUser(user) ||
    isLogisticsManagerUser(user) ||
    isLogisticsUserAccount(user) ||
    isSupportUserAccount(user)
  );
}

export function getAccessStaffDashboardRoute(user = StorageService.getUser()) {
  if (!user) return null;
  if (isSupportManagerUser(user)) return ROLE_TYPES.SUPPORT_MANAGER.route;
  if (isLogisticsManagerUser(user)) return ROLE_TYPES.LOGISTICS_MANAGER.route;
  if (isLogisticsUserAccount(user)) return ROLE_TYPES.LOGISTICS_USER.route;
  if (isSupportUserAccount(user)) return ROLE_TYPES.SUPPORT_USER.route;
  return null;
}

// Helper to check if a role ID corresponds to Quality Inspector
// Quality Inspector might be stored with different IDs in master_user_type
const QUALITY_INSPECTOR_IDS = ['quality_inspector', 'Quality Inspector'];

/**
 * Get all available roles for the current user
 * @returns {Array} Array of available role objects
 */
export function getAvailableRoles() {
  const user = StorageService.getUser();
  if (!user) return [];

  // Platform support / logistics staff are not buyer/seller trade roles.
  if (isSupportManagerUser(user)) {
    return [ROLE_TYPES.SUPPORT_MANAGER];
  }
  if (isLogisticsManagerUser(user)) {
    return [ROLE_TYPES.LOGISTICS_MANAGER];
  }
  if (isLogisticsUserAccount(user)) {
    return [ROLE_TYPES.LOGISTICS_USER];
  }
  if (isSupportUserAccount(user)) {
    return [ROLE_TYPES.SUPPORT_USER];
  }

  const availableRoles = [];
  const serviceRoleName = String(
    user?.service_role_name ||
      user?.serviceRoleName ||
      user?.servicesRole ||
      user?.services_role ||
      user?.service_role ||
      ""
  )
    .trim()
    .toLowerCase();
  const serviceRoleId = user?.service_role_id != null ? String(user.service_role_id).trim() : "";
  
  // Parse user_type_id (can be comma-separated or single value)
  let userTypeId = user.user_type_id;
  if (userTypeId === null || userTypeId === undefined) {
    userTypeId = '';
  } else {
    userTypeId = String(userTypeId).trim();
  }
  
  const typeIds = userTypeId 
    ? userTypeId.split(',').map(id => String(id).trim()).filter(id => id && id !== 'null' && id !== 'undefined')
    : [];
  
  // Check for Buyer (ID = 1)
  if (typeIds.includes('1') || userTypeId === '1') {
    availableRoles.push(ROLE_TYPES.BUYER);
  }
  
  // Check for Seller (ID = 2)
  if (typeIds.includes('2') || userTypeId === '2') {
    availableRoles.push(ROLE_TYPES.SELLER);
  }
  
  // Check for Both (ID = 3) - add both buyer and seller
  if (typeIds.includes('3') || userTypeId === '3') {
    if (!availableRoles.find(r => r.id === 1)) availableRoles.push(ROLE_TYPES.BUYER);
    if (!availableRoles.find(r => r.id === 2)) availableRoles.push(ROLE_TYPES.SELLER);
  }
  
  // Check for Quality Inspector role based on user_type_id in user_login table
  // Quality Inspector has user_type_id = 4 in master_user_type table
  // Mapping: 1 = BUYER, 2 = SELLER, 3 = BOTH, 4 = Quality Inspector, 7 = Logistics (see master_user_type)
  const hasQualityInspectorId = typeIds.includes('4') || userTypeId === '4';
  
  // If user has Quality Inspector user_type_id (4), add Quality Inspector role
  if (hasQualityInspectorId) {
    if (!availableRoles.find(r => r.id === 'quality_inspector')) {
      availableRoles.push(ROLE_TYPES.QUALITY_INSPECTOR);
    }
  }

  // Logistics: master_user_type.user_type_id = 7, and/or explicit service_role_id / name from API
  const hasLogisticsUserType = typeIds.includes('7') || userTypeId === '7';
  const logisticsByServiceRoleId = serviceRoleId === '7';
  if (
    hasLogisticsUserType ||
    logisticsByServiceRoleId ||
    serviceRoleName === 'logistics'
  ) {
    if (!availableRoles.find(r => r.id === 'logistics')) {
      availableRoles.push(ROLE_TYPES.LOGISTICS);
    }
  }
  
  return availableRoles;
}

/**
 * User account includes Seller (user_type_id 2 or 3). Same rule as sidebar auction visibility.
 * @param {object|null} [user]
 */
export function userHasSellerCapability(user = StorageService.getUser()) {
  if (!user) return false;
  let userTypeId = user.user_type_id;
  if (userTypeId === null || userTypeId === undefined) userTypeId = '';
  else userTypeId = String(userTypeId).trim();
  const typeIds = userTypeId
    ? userTypeId.split(',').map((id) => String(id).trim()).filter((id) => id && id !== 'null' && id !== 'undefined')
    : [];
  const singleId = parseInt(userTypeId, 10);
  return (
    (!Number.isNaN(singleId) && userTypeId.indexOf(',') === -1 && (singleId === 2 || singleId === 3)) ||
    typeIds.some((id) => {
      const numId = parseInt(id, 10);
      return id === '2' || id === '3' || numId === 2 || numId === 3;
    })
  );
}

/** Header role switcher is set to Seller (id 2). */
export function isActiveSellerRole() {
  const currentRole = getCurrentRole();
  if (!currentRole) return false;
  return Number(currentRole.id) === 2;
}

/** P2P Sourcing tab — show Create auction card only for seller role. */
export function canShowP2pCreateAuctionOption() {
  return userHasSellerCapability() && isActiveSellerRole();
}

/**
 * Get the currently active role
 * @returns {Object|null} Current role object or null
 */
export function getCurrentRole() {
  const storedRole = localStorage.getItem(CURRENT_ROLE_KEY) || sessionStorage.getItem(CURRENT_ROLE_KEY);
  const availableRoles = getAvailableRoles();
  
  // If there's a stored role, validate it's still in available roles
  if (storedRole) {
    try {
      const parsedRole = JSON.parse(storedRole);
      // Validate that the stored role is still in the user's available roles
      const isValidRole = availableRoles.find(r => r.id === parsedRole.id);
      if (isValidRole) {
        return parsedRole;
      } else {
        // Stored role is no longer valid, clear it
        clearCurrentRole();
      }
    } catch (e) {
      // Invalid stored role, clear it
      clearCurrentRole();
    }
  }
  
  // If no stored role or stored role is invalid, determine default based on user's roles
  if (availableRoles.length === 0) return null;
  
  // Default priority: Seller > Buyer > Logistics > Quality Inspector
  const seller = availableRoles.find(r => r.id === 2);
  if (seller) return seller;
  
  const buyer = availableRoles.find(r => r.id === 1);
  if (buyer) return buyer;

  const logistics = availableRoles.find(r => r.id === 'logistics');
  if (logistics) return logistics;
  
  return availableRoles[0]; // Return first available role
}

/**
 * Set the currently active role
 * @param {Object} role - Role object to set as active
 * @param {boolean} remember - Whether to persist in localStorage (true) or sessionStorage (false)
 */
export function setCurrentRole(role, remember = true) {
  if (!role) return;
  
  const roleStr = JSON.stringify(role);
  if (remember) {
    localStorage.setItem(CURRENT_ROLE_KEY, roleStr);
    sessionStorage.removeItem(CURRENT_ROLE_KEY);
  } else {
    sessionStorage.setItem(CURRENT_ROLE_KEY, roleStr);
    localStorage.removeItem(CURRENT_ROLE_KEY);
  }
  
  // Trigger storage event so other tabs/components can update
  window.dispatchEvent(new Event('storage'));
}

/**
 * Clear the current role
 */
export function clearCurrentRole() {
  localStorage.removeItem(CURRENT_ROLE_KEY);
  sessionStorage.removeItem(CURRENT_ROLE_KEY);
}

/**
 * Get dashboard route based on current role
 * @returns {string} Dashboard route
 */
export function getDashboardRouteByRole() {
  const accessRoute = getAccessStaffDashboardRoute();
  if (accessRoute) return accessRoute;

  const currentRole = getCurrentRole();
  if (currentRole) {
    return currentRole.route;
  }
  
  // Fallback to default logic
  const availableRoles = getAvailableRoles();
  if (availableRoles.length > 0) {
    return availableRoles[0].route;
  }
  
  return '/dashboard/buyer';
}

/**
 * Get profile route based on current role
 * @returns {string} Profile route
 */
export function getProfileRouteByRole() {
  const currentRole = getCurrentRole();
  if (currentRole) {
    return currentRole.profileRoute;
  }
  
  // Fallback to default logic
  const availableRoles = getAvailableRoles();
  if (availableRoles.length > 0) {
    return availableRoles[0].profileRoute;
  }
  
  return '/buyer-profile';
}

/**
 * Get view profile route based on current role
 * @returns {string} View profile route
 */
export function getViewProfileRouteByRole() {
  const currentRole = getCurrentRole();
  if (currentRole && currentRole.viewProfileRoute) {
    return currentRole.viewProfileRoute;
  }
  
  // Fallback to default logic
  const availableRoles = getAvailableRoles();
  if (availableRoles.length > 0 && availableRoles[0].viewProfileRoute) {
    return availableRoles[0].viewProfileRoute;
  }
  
  return '/profile/view';
}
